import api from './api';

// フォルダ機能のAPI層。
// 内部はタグ（多対多）だが、UI/UX的にはフォルダ操作として扱う。

// フォルダバーに出せるピン留めフォルダの上限（バックエンドの PIN_LIMIT と一致させること）。
export const FOLDER_PIN_LIMIT = 10;

export interface Folder {
  id: number;
  name: string;
  color: string;       // hex。空文字ならデフォルト色
  icon: string;        // アイコンキー。空文字ならデフォルト
  visibility: number;  // 0:非公開 1:公開
  order_num: number;
  pin_order: number | null;  // null:非ピン / 数値:ピン留め＋バー内の並び順
  item_count: number;
  created_at: string;
  updated_at: string;
}

export interface FolderInput {
  name: string;
  color?: string;
  icon?: string;
  visibility?: number;
}

// フォルダ一覧。username 指定時はそのユーザーの公開フォルダのみ。
export const getFolders = async (username?: string): Promise<Folder[]> => {
  const response = await api.get('/folders/', {
    params: username ? { username } : undefined,
  });
  return response.data;
};

export const createFolder = async (data: FolderInput): Promise<Folder> => {
  const response = await api.post('/folders/', data);
  return response.data;
};

export const updateFolder = async (id: number, data: Partial<FolderInput>): Promise<Folder> => {
  const response = await api.patch(`/folders/${id}/`, data);
  return response.data;
};

export const deleteFolder = async (id: number): Promise<void> => {
  await api.delete(`/folders/${id}/`);
};

// フォルダの並び順を更新（id 配列の順序で order_num を振り直す）。
export const reorderFolders = async (order: number[]): Promise<void> => {
  await api.post('/folders/reorder/', { order });
};

// フォルダをピン留め（フォルダバーに表示）。更新後のフォルダを返す。
export const pinFolder = async (id: number): Promise<Folder> => {
  const response = await api.post(`/folders/${id}/pin/`);
  return response.data;
};

// フォルダのピン留めを解除。更新後のフォルダを返す。
export const unpinFolder = async (id: number): Promise<Folder> => {
  const response = await api.post(`/folders/${id}/unpin/`);
  return response.data;
};

// ピン留めフォルダの並び順を更新（id 配列の順序で pin_order を振り直す）。
export const reorderPins = async (order: number[]): Promise<void> => {
  await api.post('/folders/reorder_pins/', { order });
};

// フォルダにゲームを一括追加。
export const addGamesToFolder = async (
  folderId: number,
  gameIds: number[],
): Promise<{ added: number; skipped: number; item_count: number; item_limit: number }> => {
  const response = await api.post(`/folders/${folderId}/add_games/`, { game_ids: gameIds });
  return response.data;
};

// フォルダからゲームを一括解除。
export const removeGamesFromFolder = async (
  folderId: number,
  gameIds: number[],
): Promise<{ removed: number; item_count: number }> => {
  const response = await api.post(`/folders/${folderId}/remove_games/`, { game_ids: gameIds });
  return response.data;
};

// 1ゲームの所属フォルダを一括設定（チェックリスト式ピッカー用）。
// item_counts は変更が起きたフォルダの確定件数（{folderId: count}）。
export const setGameFolders = async (
  gameId: number,
  folderIds: number[],
): Promise<{ folder_ids: number[]; item_counts: Record<number, number> }> => {
  const response = await api.post('/folders/set_membership/', {
    game_id: gameId,
    folder_ids: folderIds,
  });
  return response.data;
};

// 指定ゲームが所属する自分のフォルダID一覧。
export const getGameFolderIds = async (gameId: number): Promise<number[]> => {
  const response = await api.get('/folders/game_folders/', { params: { game_id: gameId } });
  return response.data.folder_ids;
};
